import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Container } from '../../Layouts/Container';
import { DashboardLayout } from '../../Layouts/DashboardLayout';
import { Wrapper } from '../../Layouts/Wrapper';
import * as Icons from "../../components/Icons";
import styles from './styles.module.scss';
import { Button } from '../../components/Button';
import Loading, { InlineLoading } from '../../components/Loading';
import { NotificationDialog } from '../../components/NotificationDialog';
import { TextInfoDialog } from '../../components/TextInfoDialog';
import { useNavigate } from '../../hook';
import { path as paymentPath } from "../PaymentOrderForm";
import {
    formatNumberWithCommas,
    getBalanceInfo,
    checkBalanceCalculation,
    calculateBalance,
    translationKeysByErrorCodes
} from '../../utils';
import { RootState } from '../../store';

export const path: string = 'tax-balance';
        
const TaxBalance = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const auth: any = useSelector((state: RootState) => state.auth);
    const utils = useSelector((state: RootState) => state.utils);

    const [recalculateProcess, setRecalculateProcess] = useState({ loading: false, notify: false, disable: true });
    const[balance, setBalance] = useState<any>({ unifiedAccountAmount: 0, taxPayment: 0, socialPayment: 0, returnAmount: 0, accountingYear: '' });
    const [helpIconDialogMessages, setHelpIconDialogMessages] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const toggleHelpIconDialogMessages = (title: string) => () => {
        setHelpIconDialogMessages(title);
    }

    const initializePageData = async () => {
        setIsLoading(true);
        try {
            const checkBalancer = await checkBalanceCalculation();
            setRecalculateProcess(prev => ({ ...prev, disable: !checkBalancer.data.value }));
            const balanceInfo = await getBalanceInfo();
            setBalance({
                unifiedAccountAmount: balanceInfo.data.unifiedAccountAmount || 0,
                taxPayment: balanceInfo.data.taxPayment || 0,
                socialPayment: balanceInfo.data.socialPayment || 0,
                returnAmount: balanceInfo.data.returnItems?.[0]?.returnAmount || 0,
                accountingYear: balanceInfo.data.returnItems?.[0]?.year,
            });
        } catch (err) {
            console.log('initializePageData >>>', err);
            const error = translationKeysByErrorCodes(err);
            if (error) {
                toast.error(t(error), { position: 'top-right', theme: 'colored' });
            }
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        initializePageData();
    }, []);

    useEffect(() => {
        if (!auth?.tin || (auth?.tin && !utils.tinIsValid)) {
            navigate('/dashboard');
        }
    }, [auth?.tin, utils.tinIsValid]);

    const onRecalculate = () => {
        if(recalculateProcess.loading || recalculateProcess.disable) return;
        setRecalculateProcess((prev) => ({ ...prev, loading: true }));
        calculateBalance()
            .then(() => setRecalculateProcess((prev) => ({ ...prev, notify: true, disable: true })))
            .catch(err => {
                console.log('calculateBalance >>>', err);
                const error = translationKeysByErrorCodes(err);
                if (error) {
                    toast.error(t(error), { position: 'top-right', theme: 'colored' });
                }
            })
            .finally(() => setRecalculateProcess(prev => ({ ...prev, loading: false })))
    };

    const goToPayment = () => {
        navigate(`/${paymentPath}`);
    };

    return (
        <DashboardLayout>
            <Container containerSize='large' withoutPadding className={styles.TaxBalance}>
                <Wrapper title={t("tax_balance")}>
                    <div className={styles.TaxBalanceWrapper}>
                        <div className={styles.Card}>
                            <span className={styles.Title}>{t("liabilities")}</span>
                            <span className={styles.subTitle}>
                                <span>{t("payable_amount")}</span>
                            </span>
                            <div className={styles.Desc}>
                                <span className={styles.Hint}>
                                    <span className={styles.Row}>
                                        <span>{t("revenueTax")}</span>
                                        <span>
                                            {formatNumberWithCommas(balance.taxPayment)}
                                            <span className={styles.ArmenianDram}>
                                                <Icons.ArmenianDram />
                                            </span>
                                        </span>
                                    </span>
                                    <span className={styles.Row}>
                                        <span>{t("socialFee")}</span>
                                        <span>
                                            {formatNumberWithCommas(balance.socialPayment)}
                                            <span className={styles.ArmenianDram}>
                                                <Icons.ArmenianDram />
                                            </span>
                                        </span>
                                    </span>
                                </span>
                            </div>
                        </div>
                        <div className={styles.Card}>
                            <span className={styles.helpIcon} onClick={toggleHelpIconDialogMessages('tax_balance_help_icon_2_title')}>
                                <Icons.InfoOutline />
                            </span>
                            <span className={styles.Title}>{t("expenses")}</span>
                            <span className={styles.subTitle}>
                                <span>{t("refundable_amount")}</span>
                            </span>
                            <div className={styles.Desc}>
                                <span className={styles.Hint}>
                                    <span className={styles.Row}>
                                        <span>{t("total")}</span>
                                        <span>
                                            {formatNumberWithCommas(balance.returnAmount)}
                                            <span className={styles.ArmenianDram}>
                                                <Icons.ArmenianDram />
                                            </span>
                                        </span>
                                    </span>
                                    <span className={styles.Row}>
                                        <span>{t("accounting_year")}</span>
                                        <span>
                                            {balance.accountingYear || '--'} {balance.accountingYear ? t("short_year_txt") : ''}
                                        </span>
                                    </span>
                                </span>
                            </div>
                        </div>
                        <div className={styles.Card}>
                            <span className={styles.helpIcon} onClick={toggleHelpIconDialogMessages('tax_balance_help_icon_1_title')}>
                                <Icons.InfoOutline />
                            </span>
                            <span className={styles.Title}>{t("single_account_balance")}</span>
                            <span className={styles.subTitle}>
                                <span>
                                    {formatNumberWithCommas(balance.unifiedAccountAmount)}
                                    <span className={styles.ArmenianDram}>
                                        <Icons.ArmenianDram />
                                    </span>
                                </span>
                            </span>
                            <div className={styles.Desc}>
                                <span className={styles.Icon}>
                                    <Icons.AccountBalance />
                                </span>
                                <span className={styles.Hint}>
                                    <span className={styles.Row}>
                                        <span />
                                        <span />
                                    </span>
                                    <span className={styles.Row}>
                                        <span></span>
                                        <span>
                                        <Button
                                            onClick={goToPayment}
                                            size='small'
                                            color='green'
                                        >
                                            {t("fill_account")}
                                        </Button>
                                        </span>
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    <div className={styles.RecalculateSection}>
                        <div className={styles.Notify}>
                            <span className={styles.IconWarning}><Icons.WarningOutlineCircle /></span>
                            <div className={styles.Text}>
                                <div dangerouslySetInnerHTML={{__html: t("obligations_payment_refund_notify")}} />
                                <div dangerouslySetInnerHTML={{__html: t("corrective_calculation_message")}} />
                            </div>
                        </div>
                        <div className={styles.Action}>
                            <Button
                                size='small'
                                disabled={recalculateProcess.disable || recalculateProcess.loading}
                                onClick={onRecalculate}
                            >
                                {t("payment_refund")} {recalculateProcess.loading && <InlineLoading size='small' />}
                            </Button>
                        </div>
                    </div>

                    <NotificationDialog
                        isOpen={recalculateProcess.notify}
                        close={() => setRecalculateProcess((prev) => ({ ...prev, notify: false }))}
                        type='warning'
                        head={t("attention")}
                        messageTranslationKey={<div dangerouslySetInnerHTML={{ __html: t('warning_message_recalculation_obligations') }} />}
                    />
                    <TextInfoDialog title={helpIconDialogMessages} onClose={toggleHelpIconDialogMessages('')} />
                    {isLoading && <Loading />}
                </Wrapper>
            </Container>
        </DashboardLayout>
    );
};

export default TaxBalance;